/**
 * Study Comparison Hook
 * 
 * Manages selection state for comparing multiple studies.
 * Limits selection to 4 studies max and provides comparison panel controls.
 */

import { useCallback, useMemo, useState } from "react";
import { toast } from "@/hooks/useToast";
import type { Study } from "@/types/api";
import { enrichStudyWithMetadata, type StudyWithMetadata } from "@/utils/studyGrouping";

// =============================================================================
// Constants
// =============================================================================

const MAX_COMPARISON_STUDIES = 4;
const MIN_COMPARISON_STUDIES = 2;

// =============================================================================
// Types
// =============================================================================

export interface UseStudyComparisonReturn {
  /** Currently selected studies for comparison */
  selectedStudies: StudyWithMetadata[];
  /** Whether the comparison panel is open */
  isComparing: boolean;
  /** Toggle selection of a study */
  toggleStudySelection: (study: StudyWithMetadata) => void;
  /** Add a study to selection */
  addStudy: (study: StudyWithMetadata) => void;
  /** Remove a study from selection */
  removeStudy: (studyId: string) => void;
  /** Select multiple studies at once (for batch comparison) */
  selectMultipleStudies: (studies: Study[]) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Open the comparison panel */
  openComparison: () => void;
  /** Close the comparison panel */
  closeComparison: () => void;
  /** Check if a study is selected */
  isStudySelected: (studyId: string) => boolean;
  /** Whether we can compare (have enough studies selected) */
  canCompare: boolean;
  /** Whether we can add more studies */
  canAddMore: boolean;
  /** Maximum number of studies for comparison */
  maxStudies: number;
}

// =============================================================================
// Hook
// =============================================================================

export function useStudyComparison(): UseStudyComparisonReturn {
  const [selectedStudies, setSelectedStudies] = useState<StudyWithMetadata[]>([]);
  const [isComparing, setIsComparing] = useState(false);

  // Check if we can compare
  const canCompare = useMemo(
    () => selectedStudies.length >= MIN_COMPARISON_STUDIES,
    [selectedStudies.length]
  );

  // Check if we can add more studies
  const canAddMore = useMemo(
    () => selectedStudies.length < MAX_COMPARISON_STUDIES,
    [selectedStudies.length]
  );

  // Check if a study is selected
  const isStudySelected = useCallback(
    (studyId: string) => selectedStudies.some((s) => s.study_id === studyId),
    [selectedStudies]
  );

  // Add a study to selection
  const addStudy = useCallback(
    (study: StudyWithMetadata) => {
      if (selectedStudies.length >= MAX_COMPARISON_STUDIES) {
        toast({
          title: "Maximum reached",
          description: `You can compare up to ${MAX_COMPARISON_STUDIES} studies at once`,
          variant: "destructive",
        });
        return;
      }

      if (isStudySelected(study.study_id)) {
        return;
      }

      setSelectedStudies((prev) => [...prev, study]);
    },
    [selectedStudies.length, isStudySelected]
  );

  // Remove a study from selection
  const removeStudy = useCallback((studyId: string) => {
    setSelectedStudies((prev) => prev.filter((s) => s.study_id !== studyId));
  }, []);

  // Toggle selection
  const toggleStudySelection = useCallback(
    (study: StudyWithMetadata) => {
      if (isStudySelected(study.study_id)) {
        removeStudy(study.study_id);
      } else {
        addStudy(study);
      }
    },
    [isStudySelected, removeStudy, addStudy]
  );

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedStudies([]);
    setIsComparing(false);
  }, []);

  // Open comparison panel
  const openComparison = useCallback(() => {
    if (!canCompare) {
      toast({
        title: "Select more studies",
        description: `Select at least ${MIN_COMPARISON_STUDIES} studies to compare`,
      });
      return;
    }
    setIsComparing(true);
  }, [canCompare]);

  // Close comparison panel
  const closeComparison = useCallback(() => {
    setIsComparing(false);
  }, []);

  // Select multiple studies at once (for batch comparison)
  const selectMultipleStudies = useCallback(
    (studies: Study[]) => {
      // Limit to max comparison studies
      const studiesToAdd = studies.slice(0, MAX_COMPARISON_STUDIES);

      if (studiesToAdd.length > MAX_COMPARISON_STUDIES) {
        toast({
          title: "Limiting selection",
          description: `Only the first ${MAX_COMPARISON_STUDIES} studies were selected`,
        });
      }

      // Enrich studies with metadata
      const enrichedStudies = studiesToAdd.map(enrichStudyWithMetadata);

      // Clear previous selection and add new studies
      setSelectedStudies(enrichedStudies);

      // Open comparison panel if we have enough studies
      if (enrichedStudies.length >= MIN_COMPARISON_STUDIES) {
        setIsComparing(true);
      }
    },
    []
  );

  return {
    selectedStudies,
    isComparing,
    toggleStudySelection,
    addStudy,
    removeStudy,
    selectMultipleStudies,
    clearSelection,
    openComparison,
    closeComparison,
    isStudySelected,
    canCompare,
    canAddMore,
    maxStudies: MAX_COMPARISON_STUDIES,
  };
}

export default useStudyComparison;









